import * as React from "react";
import { Link } from "react-router-dom";
import {
  AccessReportsAdminVersionTypeEnum,
  ManageOptions,
  SidePanelTypeEnum,
} from "@app/access-reports/admin/";
import { getElementByIdFromTemplate } from "@app/access-reports/shared/utility";
import { ColumnConfig, TextAlignment } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";

import { flatten } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import CacheProviderSets from "@app/access-reports/admin/CacheProviderSets";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";

interface AccessReportsAdminProductsTabProps {
  accessReportsWizardTemplate: AccessReportsWizardTemplate;
  disableUserActions: boolean;
  onRemoveProductsClick: (products: AccessReportsWizardProduct[]) => void;
  onSaveProductsClick: (productTypes: AccessReportsWizardOption[]) => void;
  onSaveProductTypesClick: (productTypes: AccessReportsWizardOption[]) => void;
  selectedVersionType: AccessReportsAdminVersionTypeEnum;
}

class AccessReportsAdminProductsWaffleToppings {
  public key: string;
  public product: string;
  public productType: string;
  public specialties: number;
  public groups: number;
  public edit: JSX.Element;

  constructor(
    key: string,
    product: string,
    productType: string,
    specialties: number,
    groups: number,
    edit: JSX.Element,
  ) {
    this.key = key;
    this.product = product;
    this.productType = productType;
    this.specialties = specialties;
    this.groups = groups;
    this.edit = edit;
  }
}

const AccessReportsAdminProductsTab: React.FC<AccessReportsAdminProductsTabProps> = (props: AccessReportsAdminProductsTabProps) => {
  const deleteButtonRef = React.useRef<HTMLButtonElement>(null);
  const [products, setProducts] = React.useState<AccessReportsWizardProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = React.useState<AccessReportsWizardProduct[]>([]);
  const [showRemoveProductModal, setShowRemoveProductModal] = React.useState(false);
  const [visibleSidePanel, setVisibleSidePanel] = React.useState<SidePanelTypeEnum>(null);

  React.useEffect(() => {
    if (!!props.accessReportsWizardTemplate) {
      updateProducts();
    }
  }, [props.accessReportsWizardTemplate]);

  const getAddProductLink = (): JSX.Element => {
    const productElement: AccessReportsWizardElement = getElementByIdFromTemplate(props.accessReportsWizardTemplate, AccessReportsWizardStepEnum.CoverPage, AccessReportsCoverPageElementEnum.Product);
    const id: number = !!productElement?.options.length ? Math.max(...productElement.options.map((o) => o.id)) + 1 : 0;

    return (
      <Link
        className="qes-button qes-button-secondary float-end me-3"
        id="access-reports-admin__products--add-product"
        to={{
          pathname: `/access-reports/admin/product/${id}/step/2`,
        }}
      >
        Add Product
      </Link>
    );
  }

  const getBulkActionBar = (): JSX.Element => {
    return (
      <span>
        <QESButton
          className="p-1"
          disabled={props.disableUserActions || selectedProducts.length < 1}
          qesTestId="access-reports-admin__products--delete-products"
          qesType="secondary-danger"
          onClick={() => displayRemoveProductAlert()}
          qesRef={deleteButtonRef}
        >
          <QesIcon iconName={QesIcons.TrashV1} description="Delete" className="mb3" /> Delete Selected Products
        </QESButton>
      </span>
    );
  }

  const getProductTypeProductConnections = (productTypeId: number): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = props.accessReportsWizardTemplate;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPagePageElements.find(
      x => x.elementId === AccessReportsCoverPageElementEnum.Product
    );

    let alert: JSX.Element = null;

    if (!!productElement && !!productElement.options) {
      const connectedOptions = productElement.options.filter(
        x => x.previousElementFilterId !== null && x.previousElementFilterId !== undefined && x.previousElementFilterId === productTypeId
      );

      if (connectedOptions?.length) {
        alert = (
          <QESAlert classNames="mt-3 mb-3" type="danger" persist={true}>
            Please remove the connection to the following products:
            <ul className="mb-0">
              {connectedOptions.map((connectedOption, connectedOptionIndex) => (
                <li className="mb-0" key={connectedOptionIndex}>
                  {connectedOption.displayName ? connectedOption.displayName : connectedOption.name}
                </li>
              ))}
            </ul>
          </QESAlert>
        );
      }
    }

    return alert;
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    const newSelectedProducts: AccessReportsWizardProduct[] = products.filter((p) => selectedKeys.includes(p.id.toString()));
    setSelectedProducts(newSelectedProducts);
  }

  const hideSidePanel = (): void => {
    if (visibleSidePanel) {
      setVisibleSidePanel(null);
    }
  }

  const onSaveProductsClick = (productTypes: AccessReportsWizardOption[]): void => {
    props.onSaveProductsClick(productTypes);
    hideSidePanel();
  }

  const onSaveProductTypesClick = (productTypes: AccessReportsWizardOption[]): void => {
    props.onSaveProductTypesClick(productTypes);
    hideSidePanel();
  }

  const buildWaffleToppings = (products: AccessReportsWizardProduct[]): AccessReportsAdminProductsWaffleToppings[] => {
    const waffleToppings: AccessReportsAdminProductsWaffleToppings[] = products && products.map((p, index) => {
      const key: string = p.id.toString();

      const product: string = p.displayName ? p.displayName : p.name;

      const productTypeString: string = getProductType(p);
      const productType: string = productTypeString ? productTypeString : "--";

      const specialties: number = p.accessCriterion.length;

      const groups: number = getProductGroupCount(p);

      const edit: JSX.Element = (
        <Link
          className="demibold"
          id={`access-reports-admin__products--edit-${index}`}
          to={{
            pathname: `/access-reports/admin/product/${p.id}/step/1`,
          }}
        >
          <QESButton
            qesType="secondary"
            className="p-1"
            qesTestId={`access-reports-admin__products--edit-icon-${index}`}
            title={props.disableUserActions ? "View" : "Edit"}
          >
            <QesIcon iconName={props.disableUserActions ? QesIcons.Eye : QesIcons.PencilV1} description={props.disableUserActions ? "View" : "Edit"} />
          </QESButton>
        </Link>
      );

      return new AccessReportsAdminProductsWaffleToppings(key, product, productType, specialties, groups, edit);
    });

    return waffleToppings;
  }

  const displayRemoveProductAlert = (): void => {
    setShowRemoveProductModal(true);
  }

  const getColumnConfig = (): ColumnConfig => {
    const columns: ColumnConfig = {
      edit: {
        alignment: TextAlignment.Center,
        displayText: props.disableUserActions ? "View" : "Edit",
        noResize: true,
        noSort: true,
        testId: "edit",
      },
      groups: {
        displayText: "Groups",
        noResize: true,
        noSort: true,
        testId: "groups",
      },
      product: {
        displayText: "Product",
        noResize: true,
        noSort: true,
        testId: "product",
      },
      productType: {
        displayText: "Product Type",
        noResize: true,
        noSort: true,
        testId: "productType",
      },
      specialties: {
        displayText: "Specialties",
        noResize: true,
        noSort: true,
        testId: "specialties",
      },
    };

    return columns;
  }

  const getProductGroupCount = (product: AccessReportsWizardProduct): number => {
    const groups: string[] = [];

    for (const accessCriteria of product.accessCriterion) {
      if (accessCriteria.groups) {
        for (const group of accessCriteria.groups) {
          if (!groups.includes(group)) {
            groups.push(group);
          }
        }
      }
    }

    return groups.length;
  }

  const hideRemoveProductAlert = (removeSpecialtyConfirmed: boolean): void => {
    if (removeSpecialtyConfirmed) {
      props.onRemoveProductsClick(selectedProducts);
    }

    setShowRemoveProductModal(false);
  }

  const updateProducts = (): void => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = props.accessReportsWizardTemplate;
    let newProducts: AccessReportsWizardProduct[] = [];

    if (!!accessReportsWizardTemplate) {
      const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);

      if (!!coverPage) {
        const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));
        const productElement: AccessReportsWizardElement = coverPagePageElements.find(
          x => x.elementId === AccessReportsCoverPageElementEnum.Product
        );

        if (!!productElement && !!productElement.options) {
          newProducts = productElement.options as AccessReportsWizardProduct[];
        }
      }
    }

    setProducts(newProducts);
  }

  const getProductType = (product: AccessReportsWizardProduct): string => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = props.accessReportsWizardTemplate;

    if (!!accessReportsWizardTemplate) {
      const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);

      if (!!coverPage) {
        const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));
        const productTypeElement: AccessReportsWizardElement = coverPagePageElements.find(
          x => x.elementId === AccessReportsCoverPageElementEnum.ProductType
        );

        if (!!productTypeElement && !!productTypeElement.options) {
          const productTypeOption: AccessReportsWizardOption = productTypeElement.options.find(x => x.id === product.previousElementFilterId);

          if (!!productTypeOption) {
            return productTypeOption.displayName ? productTypeOption.displayName : productTypeOption.name;
          }
        }
      }
    }
  }

  const renderCacheProviderSetsSidePanel = (): JSX.Element => {
    return (
      <CacheProviderSets />
    );
  }

  const renderManageProductsSidePanel = (): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = props.accessReportsWizardTemplate;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPagePageElements?.find(
      x => x.elementId === AccessReportsCoverPageElementEnum.Product
    );

    return (
      <ManageOptions
        disableAddingNewOptions={true}
        disableDeletion={true}
        disableNameEdit={true}
        disableUserActions={props.disableUserActions}
        getOptionConnections={getProductTypeProductConnections}
        onSaveOptionsClick={onSaveProductsClick}
        options={productElement?.options}
        optionType={AccessReportsCoverPageElementEnum.Product}
        requireMinimumOne={true}
      />
    );
  }

  const renderManageProductTypesSidePanel = (): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = props.accessReportsWizardTemplate;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productTypeElement: AccessReportsWizardElement = coverPagePageElements?.find(
      x => x.elementId === AccessReportsCoverPageElementEnum.ProductType
    );

    return (
      <ManageOptions
        disableUserActions={props.disableUserActions}
        getOptionConnections={getProductTypeProductConnections}
        onSaveOptionsClick={onSaveProductTypesClick}
        options={productTypeElement.options}
        optionType={splitKey(AccessReportsCoverPageElementEnum.ProductType)}
        requireMinimumOne={true}
      />
    );
  }

  const splitKey = (key: string): string => {
    const splitKey: string = key.replace(/([A-Z])/g, " $1");
    const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

    return formattedKey;
  }

  return (
    <div className="tab-pane fade active show" id="products">
      <Modal
        isVisible={showRemoveProductModal}
        content={{
          body: (
            <div className="overflow-auto" >
              <p>
                Are you sure you want to delete the following products:
              </p>
              <ul>
                {selectedProducts.map((selectedProduct, index) => (
                  <li className="mb-0" key={index}>
                    {selectedProduct.displayName ? selectedProduct.displayName : selectedProduct.name}
                  </li>
                ))}
              </ul>
            </div>
          ),
          cancelButtonText: "Cancel",
          confirmButtonAction: () => hideRemoveProductAlert(true),
          confirmButtonText: "Delete",
          confirmButtonType: "primary-danger",
          title: "Warning",
        }}
        closeModal={() => hideRemoveProductAlert(false)}
        hideBodyScroll={true}
        triggerElement={deleteButtonRef}
      />

      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between">
            <div>
              <FlyoutLink
                classNames="qes-button-secondary ms-3 mb-3"
                content={{
                  body: renderManageProductTypesSidePanel(),
                  title: `${props.disableUserActions ? "View" : "Manage"} Product Types`,
                }}
                panelId="access-reports-admin__products--manage-product-types"
                isVisible={visibleSidePanel === SidePanelTypeEnum.ManageProductTypes}
                size="20"
                testId="access-reports-admin__products--manage-product-types-link"
                onClose={hideSidePanel}
              >
                {props.disableUserActions ? "View" : "Manage"} Product Types
              </FlyoutLink>
              <span className="mx-3">
                |
              </span>
              <FlyoutLink
                classNames="qes-button-secondary mb-3"
                content={{
                  body: renderManageProductsSidePanel(),
                  title: `${props.disableUserActions ? "View" : "Reorder"} Products`,
                }}
                panelId="access-reports-admin__products--manage-products"
                isVisible={visibleSidePanel === SidePanelTypeEnum.ReorderProducts}
                size="20"
                testId="access-reports-admin__products--manage-products-link"
                onClose={hideSidePanel}
              >
                {props.disableUserActions ? "View" : "Reorder"} Products
              </FlyoutLink>
              {props.selectedVersionType === AccessReportsAdminVersionTypeEnum.CurrentVersion && (
                <>
                  <span className="mx-3">
                    |
                  </span>
                  <FlyoutLink
                    classNames="qes-button-secondary mb-3"
                    content={{
                      body: renderCacheProviderSetsSidePanel(),
                      title: "Cache Provider Sets",
                    }}
                    panelId="access-reports-admin__products--cache-provider-sets"
                    isVisible={visibleSidePanel === SidePanelTypeEnum.CacheProviderSets}
                    size="40"
                    testId="access-reports-admin__products--cache-provider-sets-link"
                    onClose={hideSidePanel}
                  >
                    {props.disableUserActions ? "View" : "Cache"} Provider Sets
                  </FlyoutLink>
                </>
              )}
            </div>

            {!props.disableUserActions && (
              getAddProductLink()
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {products.length ?
            <div id="access-reports-admin__products--container" className="mb-3">
              <Waffles<AccessReportsAdminProductsWaffleToppings>
                bulkActionBar={!props.disableUserActions && getBulkActionBar()}
                columns={getColumnConfig()}
                data={buildWaffleToppings(products) || []}
                fullWidth={true}
                handleCheckboxChange={handleCheckboxChange}
                selectedKeys={selectedProducts.map((p: AccessReportsWizardProduct) => p.id.toString())}
                showCheckboxes={!props.disableUserActions}
                testId="access-reports-admin__products--all-products"
              />
            </div> :
            <p className="text-center mt-3">
              No products have been defined.
            </p>
          }
        </div>
      </div>
    </div>
  );
}

export { AccessReportsAdminProductsTab };
